import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableHead, Tooltip } from '@mui/material'
import { tableStyles } from '../../../../utils/uiTokens'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { capitalize } from '../../../../utils/functions'
import { AnalyzedSubjectState, CuratorStudentAnalysisState, ExamTypeState } from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    examType: ExamTypeState
    studentsData: CuratorStudentAnalysisState[]
    isFetching: boolean
}

const AnalysisTable: React.FC<Props> = ({
    examType = 'BS',
    studentsData = [],
    isFetching,
}) => {
    const { formatMessage, locale } = useIntl()

    // ПҚС - это пересдача одного предмета, пятиколоночная раскладка БС здесь не имеет смысла
    const isPks = examType === 'PKS'
    const colSpan = isPks ? 4 : 4

    // Ментору важен прежде всего его собственный предмет
    const ownSubject = (subjects: AnalyzedSubjectState[] = []) =>
        subjects.find((subject) => subject.curatorSubject) || null

    const subjectTitle = (subject: AnalyzedSubjectState) =>
        subject[`title${capitalize(locale)}`] || subject.code || ''

    // Балл показываем только там, где есть результат: пустая ячейка честнее нуля
    const ScoreChip = ({ subject }: { subject: AnalyzedSubjectState }) => (
        <Tooltip
            placement="top"
            title={subjectTitle(subject)}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    marginRight: '6px',
                    marginBottom: '4px',
                    borderRadius: '100px',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    backgroundColor: subject.curatorSubject ? ui.pinkSoft : '#F5F3F6',
                    color: subject.curatorSubject ? ui.pink : ui.sub,
                }}
            >
                {subjectTitle(subject)}
                <b>{subject.point}</b>
            </Box>
        </Tooltip>
    )

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={tableStyles.container}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={tableStyles.head}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            {isPks ? (
                                <>
                                    <TableCell>
                                        {formatMessage({ id: 'subject' })}
                                    </TableCell>

                                    <TableCell align="center">
                                        {formatMessage({ id: 'score' })}
                                    </TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell>
                                        {formatMessage({ id: 'subjects' })}
                                    </TableCell>

                                    <TableCell align="center">
                                        {formatMessage({ id: 'overallScore' })}
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row, index) => (
                                <TableRow key={index} sx={tableStyles.row}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.firstName} {row?.lastName}
                                    </TableCell>

                                    {isPks ? (
                                        <>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                            >
                                                {ownSubject(row?.subjects)
                                                    ? subjectTitle(ownSubject(row.subjects))
                                                    : ''}
                                            </TableCell>

                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                            >
                                                {ownSubject(row?.subjects) && (
                                                    <ScoreChip subject={ownSubject(row.subjects)} />
                                                )}
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                            >
                                                {row?.subjects?.map((subject) => (
                                                    <ScoreChip
                                                        key={subject.code || subject.id}
                                                        subject={subject}
                                                    />
                                                ))}
                                            </TableCell>

                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                            >
                                                {row?.subjects?.length ? row?.totalScore : ''}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}

                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={colSpan}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default AnalysisTable
